import { useCallback, useEffect, useRef, useState } from "react"
import { useShallow } from "zustand/react/shallow"

import { GL_POSTING_ACCOUNT_TOTAL_LIMIT } from "../constants"
import { type GlPostingAccountFilters, createGlPostingAccountStore } from "../stores/useGlPostingAccountStore"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { GlAccount } from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { formatGlAccountLabel } from "@/screens/Accounting/subfeatures/ChartOfAccounts/utils/formatGlAccountLabel"

export interface GlPostingAccountOption {
  value: string
  label: string
}

interface UseGlPostingAccountOptionsReturn {
  options: GlPostingAccountOption[]
  isFetching: boolean
  hasMore: boolean
  onQueryChange: (query: string) => void
  onLoadMore: () => void
  ensureOption: (option: GlPostingAccountOption) => void
}

const toOption = (account: GlAccount): GlPostingAccountOption => ({
  value: account.glAccountId ?? "",
  label: formatGlAccountLabel(account),
})

export const useGlPostingAccountOptions = (
  initialOptions?: GlPostingAccountOption[]
): UseGlPostingAccountOptionsReturn => {
  // One store instance per hook call (per transaction line row) — see createGlPostingAccountStore.
  const [useAccountStore] = useState(() => createGlPostingAccountStore())

  const { data, fetching, hasMore, executeSearch, loadMore, clearStore } = useAccountStore(
    useShallow(({ data, fetching, hasMore, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      hasMore,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const [query, setQuery] = useState("")
  const [stickyOptions, setStickyOptions] = useState<GlPostingAccountOption[]>(initialOptions ?? [])
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    executeSearch(queryClient, { query: query || undefined } as GlPostingAccountFilters)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      clearStore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onQueryChange = useCallback(
    (next: string) => {
      setQuery(next)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        executeSearch(queryClient, { query: next || undefined } as GlPostingAccountFilters)
      }, DEBOUNCE_DELAY_MS)
    },
    [executeSearch]
  )

  const onLoadMore = useCallback(() => {
    if (!hasMore || fetching || data.length >= GL_POSTING_ACCOUNT_TOTAL_LIMIT) return
    loadMore(queryClient, { query: query || undefined } as GlPostingAccountFilters)
  }, [hasMore, fetching, data.length, loadMore, query])

  const ensureOption = useCallback((option: GlPostingAccountOption) => {
    setStickyOptions((prev) => (prev.some((o) => o.value === option.value) ? prev : [...prev, option]))
  }, [])

  const mappedOptions = data.map(toOption)

  const options = [
    ...stickyOptions.filter((sticky) => !mappedOptions.some((o) => o.value === sticky.value)),
    ...mappedOptions,
  ]

  return { options, isFetching: fetching, hasMore, onQueryChange, onLoadMore, ensureOption }
}
